import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Clock, 
  User, 
  UserCheck, 
  Calendar, 
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Star,
  Eye,
  ArrowRight,
  Filter,
  RefreshCw,
  Briefcase,
  CalendarPlus
} from 'lucide-react'
import { Link } from 'react-router-dom'

interface ActivityItem {
  id: string
  type: 'application' | 'interview_completed' | 'candidate_shortlisted' | 'position_update' | 'position_created' | 'interview_scheduled'
  message: string
  timestamp: string
  candidateId?: string
  positionId?: string
  candidateName?: string
  positionTitle?: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
}

interface ActivityFeedProps {
  activities: ActivityItem[]
  loading?: boolean
  onRefresh?: () => void
  showFilters?: boolean
}

export default function ActivityFeed({ 
  activities = [], 
  loading = false, 
  onRefresh,
  showFilters = true 
}: ActivityFeedProps) {
  const [filter, setFilter] = useState<string>('all')
  const [filteredActivities, setFilteredActivities] = useState<ActivityItem[]>(activities)

  useEffect(() => {
    if (filter === 'all') {
      setFilteredActivities(activities)
    } else {
      setFilteredActivities(activities.filter(activity => activity.type === filter))
    }
  }, [activities, filter])

  const getActivityIcon = (type: string, priority: string) => {
    const iconClass = `w-4 h-4 ${getPriorityColor(priority)}`
    
    switch (type) {
      case 'application':
        return <User className={iconClass} />
      case 'interview_completed':
        return <Calendar className={iconClass} />
      case 'interview_scheduled':
        return <CalendarPlus className={iconClass} />
      case 'candidate_shortlisted':
        return <UserCheck className={iconClass} />
      case 'position_update':
        return <TrendingUp className={iconClass} />
      case 'position_created':
        return <Briefcase className={iconClass} />
      default:
        return <Clock className={iconClass} />
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600'
      case 'high':
        return 'text-orange-600'
      case 'medium':
        return 'text-blue-600'
      case 'low':
        return 'text-gray-600'
      default:
        return 'text-gray-600'
    }
  }

  const getPriorityBorder = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'border-l-red-500'
      case 'high':
        return 'border-l-orange-500'
      case 'medium':
        return 'border-l-blue-500'
      case 'low':
        return 'border-l-gray-400'
      default:
        return 'border-l-gray-400'
    }
  }

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date()
    const time = new Date(timestamp)
    const diffInSeconds = Math.floor((now.getTime() - time.getTime()) / 1000)

    if (diffInSeconds < 60) {
      return 'Just now'
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60)
      return `${minutes}m ago`
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600)
      return `${hours}h ago`
    } else {
      const days = Math.floor(diffInSeconds / 86400)
      return `${days}d ago`
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
          <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
        </div>
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-start space-x-3 animate-pulse">
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
        <div className="flex items-center space-x-2">
          {showFilters && (
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Events</option>
              <option value="application">Applications</option>
              <option value="interview_completed">Interviews</option>
              <option value="interview_scheduled">Scheduled Interviews</option>
              <option value="position_created">New Positions</option>
              <option value="candidate_shortlisted">Shortlisted</option>
            </select>
          )}
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {filteredActivities.length === 0 ? (
        <div className="text-center py-8">
          <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h4 className="text-sm font-medium text-gray-900 mb-2">No Recent Activity</h4>
          <p className="text-sm text-gray-600">
            {filter === 'all' 
              ? 'No recent activity to display.' 
              : `No ${filter.replace('_', ' ')} activity to display.`
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {filteredActivities.map((activity, index) => (
            <motion.div
              key={activity.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`flex items-start space-x-3 p-3 rounded-lg border-l-4 bg-gray-50 hover:bg-gray-100 transition-colors ${getPriorityBorder(activity.priority)}`}
            >
              <div className="flex-shrink-0 mt-1">
                {getActivityIcon(activity.type, activity.priority)}
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900">{activity.message}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-gray-500">
                    {formatTimeAgo(activity.timestamp)}
                  </p>
                  <div className="flex items-center space-x-2">
                    {activity.candidateId && (
                      <Link
                        to={`/recruiter/candidates/${activity.candidateId}`}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View Candidate
                      </Link>
                    )}
                    {activity.positionId && (
                      <Link
                        to={`/recruiter/positions/${activity.positionId}`}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View Position
                      </Link>
                    )}
                  </div>
                </div>
              </div>

              {activity.priority === 'urgent' && (
                <div className="flex-shrink-0">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {filteredActivities.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <Link
            to="/recruiter/activity"
            className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            View All Activity
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      )}
    </div>
  )
}